import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Loading from "../common/Loading";
import "./SavedJobs.css";  // Assume CSS file exists or create if needed
import { getSavedJobs, removeSavedJob } from '../../../../data-calls/candidate';
import { getProfile } from '../../../../helpers/hasToken';
import toastr from "toastr";

import locationIcon from '../../../img/UserProfile/location-icon-purple.webp';
import companyLogo from '../../../img/UserProfile/CompanyLogo.webp';
import postedDateIcon from '../../../img/UserProfile/posted-date.webp';
import searchIcon from '../../../img/search-icon.webp';

const SavedJobs = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [savedJobs, setSavedJobs] = useState([]);
  const profile = getProfile();

  useEffect(() => {
    fetchSavedJobs();
  }, []);

  const fetchSavedJobs = async () => {
    if (!profile || profile.type !== 'candidate') {
      setLoading(false);
      return;
    }
    try {
      const res = await getSavedJobs();
      if (res.status === 200 && res.data.success) {
        setSavedJobs(res.data.jobs);
      } else {
        toastr.options = {
          positionClass: 'toast-bottom-center',
          timeOut: 1500,
          extendedTimeOut: 500,
          closeButton: false,
          tapToDismiss: true,
        };
        toastr.error(res.data.message || "Failed to fetch saved jobs.");
      }
    } catch (error) {
      console.error("Error fetching saved jobs:", error);
      toastr.options = {
        positionClass: 'toast-bottom-center',
        timeOut: 1500,
        extendedTimeOut: 500,
        closeButton: false,
        tapToDismiss: true,
      };
      toastr.error("An error occurred while fetching saved jobs.");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveJob = async (jobId) => {
    if (!profile || profile.type !== 'candidate') {
      toastr.options = {
        positionClass: 'toast-bottom-center',
        timeOut: 1500,
        extendedTimeOut: 500,
        closeButton: false,
        tapToDismiss: true,
      };
      toastr.error("Please log in as a candidate.");
      return;
    }
    try {
      const res = await removeSavedJob(jobId);
      if (res.status === 200 && res.data.success) {
        setSavedJobs(prev => prev.filter(job => job.jobId !== jobId));
        toastr.options = {
          positionClass: 'toast-bottom-center',
          timeOut: 1500,
          extendedTimeOut: 500,
          closeButton: false,
          tapToDismiss: true,
        };
        toastr.success(res.data.message);
      } else {
        toastr.options = {
          positionClass: 'toast-bottom-center',
          timeOut: 1500,
          extendedTimeOut: 500,
          closeButton: false,
          tapToDismiss: true,
        };
        toastr.error(res.data.message || "Failed to remove saved job.");
      }
    } catch (error) {
      console.error("Error removing saved job:", error);
      toastr.options = {
        positionClass: 'toast-bottom-center',
        timeOut: 1500,
        extendedTimeOut: 500,
        closeButton: false,
        tapToDismiss: true,
      };
      toastr.error("An error occurred while removing the saved job.");
    }
  };

  const filteredJobs = savedJobs.filter(job =>
    job.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <Loading message="Loading Saved Jobs..." />;
  }

  return (
    <div className="savedjobs-content">
        {/* Header with search */}
        <div className="Tot-Saved-Job">
          <div className="NameText">
            <h1>
              <strong>Saved Jobs ({filteredJobs.length})</strong>
            </h1>
          </div>
          <div className="TR-searchbox">
            <input
              type="text"
              placeholder="Search in saved Jobs"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <img
              src={searchIcon}
              alt="search"
              className="search-icon"
            />
          </div>
        </div>

        {/* Render saved job cards */}
        {filteredJobs.length === 0 ? (
          <div className="no-jobs-message">
            <p><span className="first-part">You haven't saved any jobs yet.</span> <br /><span className="second-part">Start exploring opportunities & save jobs that match your skills!</span></p>
          </div>
        ) : (
          filteredJobs.map((job) => (
            <div key={job.jobId} className="savedjobs-card">
              <div className="CardParent">
                <div className="CardChild-left">
                  <h2>{job.title}</h2>
                  <div className="JoB-Details">
                    <div className="JOBID MB-10">
                      <p>
                        <strong>Job ID:</strong> {job.jobId}
                      </p>
                    </div>
                    <div className="Statecity MB-10">
                      <picture>
                        <source srcSet={locationIcon} type="image/webp" />
                        <source srcSet={locationIcon} type="image/png" />
                        <img
                          src={locationIcon}
                          alt="location icon"
                          className="icon"
                        />
                      </picture>
                      <p>{job.location}</p>
                    </div>
                    <div className="Role-forJob MB-10">
                      <p>{job.category}</p>
                    </div>
                    <div className="USACompanyName MB-10">
                      <p>{job.tenantName}</p>
                    </div>
                    <div className="postedDate MB-10">
                      <picture>
                        <source srcSet={postedDateIcon} type="image/webp" />
                        <source srcSet={postedDateIcon} type="image/png" />
                        <img
                          src={postedDateIcon}
                          alt="posted date"
                          className="icon"
                        />
                      </picture>
                      <p>
                        Posted on: <strong>{new Date(job.created).toLocaleDateString()}</strong>
                      </p>
                    </div>
                  </div>
                </div>
                <div className="CardChild-right">
                  <div className="job-actions">
                    <button
                      className="apply-btn"
                      disabled={!job.isActive}
                      title={!job.isActive ? "This job is currently inactive." : ""}
                      onClick={() => job.isActive && navigate(`/job-details/${job.jobId}/${job.tenantId}`)}
                    >
                      View Job Details
                    </button>
                    <button className="delete-btn" onClick={() => handleRemoveJob(job.jobId)}>
                      <svg className="remove-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 6H5H21" stroke="redColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
    </div>
  );
};

export default SavedJobs;
